import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { ActivityQueriesFilter, PaginatedActivity } from 'types/activity'
import { fetchActivityLogs } from '../../methods'

export function useActivityQueriesInfinite(
  filter?: ActivityQueriesFilter,
  options?: {
    queryOptions?: UseInfiniteQueryOptions<any>
    enabled?: boolean
  }
) {
  /* --------------------------------- States --------------------------------- */
  const infiniteQueryRef = useRef<HTMLDivElement>(null)
  const pageSize = filter?.limit || 20

  /* --------------------------------- Memoized Values -------------------------------- */
  // Memoize the query key to prevent unnecessary re-renders
  const queryKey = useMemo(() => ['activityLogs', filter], [filter])

  const query = useInfiniteQuery<PaginatedActivity>({
    queryKey,
    queryFn: ({ pageParam = 1 }) => {
      // Add pageSize and current page to the filter
      const queryFilter = {
        ...filter,
        limit: pageSize,
        page: pageParam as number,
      }

      return fetchActivityLogs({
        filter: queryFilter,
      })
    },
    getNextPageParam: (lastPage: PaginatedActivity) => {
      // If API explicitly tells us there are more pages
      if (lastPage.pagination?.hasMore) {
        return lastPage.pagination.currentPage + 1
      }

      // If we got a full page of results but hasMore is not explicitly set,
      // assume there might be more data
      if (
        lastPage.data.length === pageSize &&
        lastPage.pagination?.hasMore === undefined
      ) {
        return (lastPage.pagination?.currentPage || 0) + 1
      }

      // No more pages
      return undefined
    },
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes before refetching
    refetchOnWindowFocus: false,
    enabled: options?.enabled !== false, // Enabled by default unless explicitly disabled
    ...options?.queryOptions,
  })

  /* --------------------------------- Methods -------------------------------- */
  /**
   * IntersectionObserver callback for infinite scrolling
   * Triggers fetchNextPage when the target element becomes visible
   */
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries

      // Only fetch next page if:
      // 1. The element is visible
      // 2. There are more pages available
      // 3. We're not already fetching the next page
      if (
        entry.isIntersecting &&
        query.hasNextPage &&
        !query.isFetchingNextPage
      ) {
        query.fetchNextPage()
      }
    },
    [query.fetchNextPage, query.hasNextPage, query.isFetchingNextPage]
  )

  /* --------------------------------- Watches -------------------------------- */
  /**
   * Set up the IntersectionObserver for infinite scrolling
   * This attaches the observer to the infiniteQueryRef element
   */
  useEffect(() => {
    // If there's no more data to load or fetching is disabled, don't set up an observer
    if (!query.hasNextPage || options?.enabled === false) return

    // Configure and create the observer
    const observer = new IntersectionObserver(handleObserver, {
      root: null, // Use the viewport as root
      rootMargin: '100px', // Start loading when element is 100px from being visible
      threshold: 0.1, // Trigger when at least 10% of the element is visible
    })

    // Attach observer to the reference element if it exists
    if (infiniteQueryRef.current) {
      observer.observe(infiniteQueryRef.current)
    }

    // Clean up observer on unmount or when dependencies change
    return () => {
      if (infiniteQueryRef.current) {
        observer.unobserve(infiniteQueryRef.current)
      }
      observer.disconnect()
    }
  }, [handleObserver, query.hasNextPage, options?.enabled])

  /**
   * Flattens all pages of activity logs into a single array for easier rendering
   */
  const allActivities = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) || [],
    [query.data?.pages]
  )

  /**
   * Helper to determine if there's any meaningful data available
   */
  const hasActivities = useMemo(() => allActivities.length > 0, [allActivities])

  /**
   * Helper to determine total count of activities across all pages
   */
  const totalActivities = useMemo(
    () =>
      query.data?.pages.reduce(
        (sum: number, page) => sum + page.data.length,
        0
      ) || 0,
    [query.data?.pages]
  )

  return {
    ...query,
    infiniteQueryRef,
    allActivities,
    hasActivities,
    totalActivities,
  }
}
